import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Flex,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiDollarSign,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [expenseStats, setExpenseStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadStatistics = async () => {
      try {
        console.log('Loading dashboard statistics...');
        
        // Charger les statistiques principales
        const mainStats = await apiService.getStatistics();
        console.log('Main stats received:', mainStats);
        
        // Charger les statistiques de communion
        let communionParticipants = 0;
        try {
          const communionStats = await apiService.makeRequest('/communion/statistics');
          console.log('Communion stats received:', communionStats);
          communionParticipants = communionStats?.nextCommunion?.participants || 0;
        } catch (communionError) {
          console.error('Error loading communion stats:', communionError);
        }

        // Charger les statistiques des dépenses
        let expensesData = null;
        try {
          const currentMonth = new Date().getMonth() + 1;
          const currentYear = new Date().getFullYear();
          const expensesResponse = await apiService.getExpenses({ 
            month: currentMonth, 
            year: currentYear 
          });
          
          // Calculer le total
          const totalExpenses = expensesResponse.expenses.reduce((sum, expense) => 
            sum + parseFloat(expense.montant), 0
          );
          
          expensesData = {
            total: totalExpenses,
            count: expensesResponse.expenses.length,
            month: currentMonth,
            year: currentYear
          };
          console.log('Expense stats:', expensesData);
        } catch (expenseError) {
          console.error('Error loading expense stats:', expenseError);
        }
        
        setStats({
          ...mainStats,
          communionParticipants
        });
        
        setExpenseStats(expensesData);
        
        console.log('Final stats:', {
          ...mainStats,
          communionParticipants,
          expenses: expensesData
        });
      } catch (err) {
        console.error('Error loading statistics:', err);
        setError(err.message || 'Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, []);

  const StatCard = ({ title, stat, icon, color = 'blue', helpText, trend, onClick }) => (
    <Card cursor={onClick ? 'pointer' : 'default'} onClick={onClick} _hover={onClick ? { shadow: 'md' } : {}}>
      <CardBody>
        <Flex justify="space-between" align="flex-start">
          <Stat>
            <StatLabel color="gray.500">{title}</StatLabel>
            <StatNumber color={`${color}.500`} fontSize="2xl">
              {stat}
            </StatNumber>
            {helpText && (
              <StatHelpText>
                {trend && <StatArrow type={trend > 0 ? 'increase' : 'decrease'} />}
                {helpText}
              </StatHelpText>
            )}
          </Stat>
          <Box
            p={3}
            borderRadius="md"
            bg={`${color}.100`}
            color={`${color}.500`}
          >
            <Icon as={icon} w={6} h={6} />
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Flex justify="center" align="center" height="200px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const getMonthName = (month) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[month - 1];
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Calculer le solde (revenus - dépenses)
  const balance = (parseFloat(stats?.monthlyContributions?.total) || 0) - (expenseStats?.total || 0);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading color="blue.500">
          Tableau de bord
        </Heading>
        <Button 
          colorScheme="blue" 
          size="sm"
          onClick={() => navigate('/expenses')}
        >
          Voir toutes les dépenses
        </Button>
      </Flex>

      <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={6} mb={8}>
        <GridItem>
          <StatCard
            title="Membres actifs"
            stat={stats?.totalMembers || 0}
            icon={FiUsers}
            helpText="Total des membres"
          />
        </GridItem>

        <GridItem>
          <StatCard
            title="Revenus ce mois"
            stat={formatAmount(stats?.monthlyContributions?.total || 0)}
            icon={FiTrendingUp}
            color="green"
            helpText={`${stats?.monthlyContributions?.count || 0} contributions`}
            trend={stats?.monthlyContributions?.trend}
          />
        </GridItem>

        <GridItem>
          <StatCard
            title="Dépenses ce mois"
            stat={formatAmount(expenseStats?.total || 0)}
            icon={FiTrendingDown}
            color="red"
            helpText={`${expenseStats?.count || 0} dépense(s) - ${getMonthName(expenseStats?.month || new Date().getMonth() + 1)}`}
            onClick={() => navigate('/expenses')}
          />
        </GridItem>

        <GridItem>
          <StatCard
            title="Solde du mois"
            stat={formatAmount(balance)}
            icon={balance >= 0 ? FiTrendingUp : FiTrendingDown}
            color={balance >= 0 ? 'green' : 'orange'}
            helpText={balance >= 0 ? 'Excédent' : 'Déficit'}
          />
        </GridItem>

        <GridItem>
          <StatCard
            title="Cultes ce mois"
            stat={stats?.monthlyWorshipSessions || 0}
            icon={FiCalendar}
            color="blue"
            helpText="Sessions enregistrées"
          />
        </GridItem>

        <GridItem>
          <StatCard
            title="Participants Sainte Cène"
            stat={stats?.communionParticipants || 0}
            icon={FiCheckCircle}
            color="purple"
            helpText="Prochaine session"
          />
        </GridItem>
      </Grid>

      {/* Résumé financier */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} mb={6}>
        <GridItem>
          <Card>
            <CardHeader bg="green.50">
              <Heading size="md" color="green.700">
                💰 Revenus totaux
              </Heading>
            </CardHeader>
            <CardBody>
              <Stat>
                <StatNumber fontSize="3xl" color="green.600">
                  {formatAmount(stats?.totalRevenue || 0)}
                </StatNumber>
                <StatHelpText>Toutes catégories confondues</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardHeader bg="red.50">
              <Heading size="md" color="red.700">
                📉 Dépenses totales
              </Heading>
            </CardHeader>
            <CardBody>
              <Stat>
                <StatNumber fontSize="3xl" color="red.600">
                  {formatAmount(expenseStats?.total || 0)}
                </StatNumber>
                <StatHelpText>
                  {getMonthName(expenseStats?.month || new Date().getMonth() + 1)} {expenseStats?.year}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="md">Activité récente</Heading>
            </CardHeader>
            <CardBody>
              {stats?.recentActivity?.length > 0 ? (
                stats.recentActivity.map((activity, index) => (
                  <Box key={index} mb={4} p={3} bg="gray.50" borderRadius="md">
                    <Text fontWeight="semibold">{activity.title}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {activity.description}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {new Date(activity.date).toLocaleDateString('fr-FR')}
                    </Text>
                  </Box>
                ))
              ) : (
                <Text color="gray.500">Aucune activité récente</Text>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="md">Statistiques mensuelles</Heading>
            </CardHeader>
            <CardBody>
              <Box mb={4}>
                <Text fontWeight="semibold" mb={2}>
                  Répartition des cotisations
                </Text>
                {stats?.contributionsByType?.length > 0 ? (
                  stats.contributionsByType.map((contrib, index) => (
                    <Flex key={index} justify="space-between" mb={2}>
                      <Text fontSize="sm">{contrib.type}</Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {formatAmount(contrib.amount)} ({contrib.count})
                      </Text>
                    </Flex>
                  ))
                ) : (
                  <Text fontSize="sm" color="gray.500">Aucune donnée disponible</Text>
                )}
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Participation moyenne aux cultes
                </Text>
                <Text fontSize="lg" color="blue.500">
                  {stats?.averageWorshipAttendance || 0} personnes
                </Text>
              </Box>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Dashboard;